<template>
  <ChatLayout @nav-change="handleNavChange">
    <!-- 导航栏用户头像 -->
    <template #nav-user-avatar>
      <el-avatar :size="40" :src="currentUser.avatar" class="cursor-pointer">
        {{ currentUser.name.charAt(0) }}
      </el-avatar>
    </template>

    <!-- 应用标题 -->
    <template #app-title>
      <div class="text-sm font-medium text-gray-800">小兔</div>
    </template>
    
    <!-- 用户头像 -->
    <template #user-avatar>
      <el-avatar :size="28" :src="currentUser.avatar" class="mr-2">
        {{ currentUser.name.charAt(0) }}
      </el-avatar>
      <div>
        <div class="font-medium text-xs">{{ currentUser.name }}</div>
      </div>
    </template>

    <!-- Tab 切换组件 -->
    <template #chat-tabs>
      <el-tabs v-model="activeTabName" class="chat-tabs">
        <el-tab-pane label="私聊" name="private">
          <!-- 标签内容由下方的联系人列表提供 -->
        </el-tab-pane>
        <el-tab-pane label="群聊" name="group">
          <!-- 标签内容由下方的联系人列表提供 -->
        </el-tab-pane>
      </el-tabs>
    </template>
    
    <!-- 联系人列表 -->
    <template #contacts-list>
      <!-- 根据当前选中的Tab渲染不同的联系人列表 -->
      <div>
        <!-- 私聊联系人 -->
        <template v-if="activeTabName === 'private'">
          <div 
            v-for="contact in privateContacts" 
            :key="contact.id"
            class="px-3 py-2 border-b border-gray-50 flex items-center hover:bg-gray-50 cursor-pointer"
            :class="{'bg-blue-50': activeContact?.id === contact.id}"
            @click="selectContact(contact)"
          >
            <el-avatar :size="36" :src="contact.avatar" class="mr-3">
              {{ contact.name.charAt(0) }}
            </el-avatar>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-medium text-gray-900 truncate">{{ contact.name }}</h3>
                <span class="text-xs text-gray-500 ml-2">{{ formatTime(contact.lastMessageTime) }}</span>
              </div>
              <div class="flex items-center justify-between mt-0.5">
                <p class="text-xs text-gray-500 truncate">{{ contact.lastMessage }}</p>
                <span v-if="contact.unreadCount > 0" class="ml-2 min-w-[16px] h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center px-1">
                  {{ contact.unreadCount > 99 ? '99+' : contact.unreadCount }}
                </span>
              </div>
            </div>
          </div>
          
          <!-- 私聊中的其他联系人 -->
          <template v-if="showOtherContactsInPrivate">
            <template v-for="(contact, index) in otherContacts" :key="contact.id">
              <!-- 其他分类标题 -->
              <div v-if="index === 0" class="px-3 py-1 bg-gray-50 border-b border-gray-100">
                <div class="font-medium text-xs text-gray-500">其他</div>
              </div>
              
              <div 
                class="px-3 py-2 border-b border-gray-50 flex items-center hover:bg-gray-50 cursor-pointer"
                :class="{'bg-blue-50': activeContact?.id === contact.id}"
                @click="selectContact(contact)"
              >
                <div class="relative flex-shrink-0">
                  <el-avatar :size="36" :src="contact.avatar" class="bg-gray-200">
                    {{ contact.name.charAt(0) }}
                  </el-avatar>
                  <div v-if="contact.online" class="absolute bottom-0 right-0 w-2 h-2 bg-green-500 rounded-full border border-white"></div>
                </div>
                <div class="ml-3 flex-1 overflow-hidden">
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <div class="font-medium text-sm text-gray-900">{{ contact.name }}</div>
                      <div v-if="contact.subtitle" class="text-xs text-gray-400">{{ contact.subtitle }}</div>
                      <div v-if="contact.lastMessage" class="text-xs text-gray-500 truncate mt-0.5">{{ contact.lastMessage }}</div>
                    </div>
                    <div v-if="contact.lastMessageTime" class="text-xs text-gray-400 ml-2 flex-shrink-0">
                      {{ formatTime(contact.lastMessageTime) }}
                    </div>
                  </div>
                </div>
                <div v-if="contact.unreadCount" class="bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center ml-2 flex-shrink-0">
                  {{ contact.unreadCount > 99 ? '99+' : contact.unreadCount }}
                </div>
              </div>
            </template>
          </template>
        </template>
        
        <!-- 群聊联系人 -->
        <template v-else-if="activeTabName === 'group'">
          <div 
            v-for="group in groupContacts" 
            :key="group.id"
            class="px-3 py-2 border-b border-gray-50 flex items-center hover:bg-gray-50 cursor-pointer"
            :class="{'bg-blue-50': activeContact?.id === group.id}"
            @click="selectContact(group)"
          >
            <el-avatar :size="36" :src="group.avatar" class="mr-3">
              {{ group.name.charAt(0) }}
            </el-avatar>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-medium text-gray-900 truncate">{{ group.name }}</h3>
                <span class="text-xs text-gray-500 ml-2">{{ formatTime(group.lastMessageTime) }}</span>
              </div>
              <div class="flex items-center justify-between mt-0.5">
                <p class="text-xs text-gray-500 truncate">{{ group.lastMessage }}</p>
                <span v-if="group.unreadCount > 0" class="ml-2 min-w-[16px] h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center px-1">
                  {{ group.unreadCount > 99 ? '99+' : group.unreadCount }}
                </span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>

    <!-- 聊天头像 -->
    <template #chat-avatar>
      <el-avatar v-if="activeContact" :size="36" :src="activeContact.avatar">
        {{ activeContact.name.charAt(0) }}
      </el-avatar>
    </template>

    <!-- 聊天标题 -->
    <template #chat-title>
      <span v-if="activeContact">{{ activeContact.name }}</span>
    </template>

    <!-- 聊天副标题 -->
    <template #chat-subtitle>
      <div class="flex items-center space-x-2">
        <span v-if="activeContact">{{ activeContact.status || '在线' }}</span>
        <!-- 连接状态指示器 -->
        <div class="flex items-center space-x-1">
          <div :class="[
            'w-2 h-2 rounded-full',
            isConnected ? 'bg-green-500' : 'bg-red-500'
          ]"></div>
          <span class="text-xs text-gray-500">{{ connectionStatus }}</span>
        </div>
        <!-- 输入状态显示 -->
        <span v-if="typingStatusText" class="text-xs text-gray-500 italic">{{ typingStatusText }}</span>
      </div>
    </template>

    <!-- 聊天消息 -->
    <template #messages>
      <div class="space-y-3">
        <div 
          v-for="message in messages" 
          :key="message.id"
          class="flex"
          :class="{ 'justify-end': message.isSelf, 'justify-start': !message.isSelf }"
        >
          <!-- 对方消息 -->
          <div v-if="!message.isSelf" class="flex items-start space-x-2 max-w-[70%]">
            <el-avatar :size="28" :src="message.avatar" class="flex-shrink-0">
              {{ message.sender.charAt(0) }}
            </el-avatar>
            <div>
              <div class="text-xs text-gray-500 mb-1">{{ message.sender }} {{ formatMessageTime(message.timestamp) }}</div>
              <div class="bg-white rounded-tr-xl rounded-tl-sm rounded-br-xl rounded-bl-xl px-3 py-2 shadow-sm relative">
                <div class="absolute -left-1 top-3 w-0 h-0 border-r-[6px] border-r-white border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
                <div class="text-sm text-gray-800">{{ message.content }}</div>
              </div>
            </div>
          </div>

          <!-- 自己的消息 -->
          <div v-else class="flex items-start space-x-2 max-w-[70%] flex-row-reverse">
            <el-avatar :size="28" :src="currentUser.avatar" class="flex-shrink-0">
              {{ currentUser.name.charAt(0) }}
            </el-avatar>
            <div class="text-right">
              <div class="text-xs text-gray-500 mb-1">{{ formatMessageTime(message.timestamp) }}</div>
              <div class="bg-green-500 text-white rounded-tl-xl rounded-tr-sm rounded-bl-xl rounded-br-xl px-3 py-2 shadow-sm relative">
                <div class="absolute -right-1 top-3 w-0 h-0 border-l-[6px] border-l-green-500 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
                <div class="text-sm">{{ message.content }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- 消息输入框 -->
    <template #message-input>
      <div class="space-y-2">
        <!-- 输入框 -->
        <div>
          <el-input
            v-model="newMessage"
            type="textarea"
            :rows="5"
            placeholder="输入消息..."
            class="w-full"
            @keydown.enter="handleEnterPress"
            @keydown.ctrl.enter="sendMessage"
            @input="handleInputChange"
          />
        </div>
        
        <!-- 工具栏和发送按钮 -->
        <div class="flex items-center justify-between">
          <!-- 工具栏按钮 -->
          <div class="flex items-center space-x-2 text-gray-500">
            <button class="p-1 hover:text-gray-700 hover:bg-gray-100 rounded">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>
            <button class="p-1 hover:text-gray-700 hover:bg-gray-100 rounded">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
            <button class="p-1 hover:text-gray-700 hover:bg-gray-100 rounded">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
          
          <!-- 发送按钮 -->
          <el-button
            type="primary"
            @click="sendMessage" 
            :disabled="!newMessage.trim()"
            class="disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            发送
          </el-button>
        </div>
      </div>
    </template>
  </ChatLayout>
</template>

<script setup>
import dayjs from 'dayjs'
import ChatLayout from '@/layouts/Chat.vue'
import { useChat } from '@/hooks/useChat'

// 组件数据
const currentUser = ref({
  id: 1,
  name: '管理员',
  avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png'
})

// Tab 相关状态
const activeTabName = ref('private') // 默认显示私聊标签
const showOtherContactsInPrivate = ref(true) // 在私聊模式下是否显示其他联系人

const activeContact = ref(null)
const newMessage = ref('')

const instance = getCurrentInstance()

// 初始化聊天功能
const {
  isConnected,
  connectionStatus,
  chatMessages,
  typingUsers,
  onlineUsers,
  connect,
  disconnect,
  subscribeToChat,
  sendMessage: sendChatMessage,
  markAsRead,
  sendTypingStatus,
  loadConversationMessages,
  scrollToBottom,
  handleReceivedMessage,
  getChannelConfig
} = useChat({
  userId: currentUser.value?.id || 1,
  onMessageReceived: (message) => {
    // 处理接收到的新消息
    console.log('收到新消息:', message)
    // 更新联系人列表中的最后消息
    updateContactLastMessage(message)
  },
  onUserStatusChanged: (data) => {
    // 处理用户状态变化
    console.log('用户状态变化:', data)
  },
  onTypingStatusChanged: (data) => {
    // 处理输入状态变化
    console.log('输入状态变化:', data)
  }
}) || {}

// 当前用户ID（这里需要从实际的用户状态中获取）
const currentUserId = ref(1) // 临时硬编码，实际应该从用户状态获取

// 私聊联系人列表
const privateContacts = ref([
  {
    id: 1,
    name: '张三',
    avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
    lastMessage: '你好，有什么可以帮助你的吗？',
    lastMessageTime: new Date(Date.now() - 300000), // 5分钟前
    unreadCount: 2,
    status: '在线',
    online: true,
    conversation_id: 'private_1'
  },
  {
    id: 2,
    name: '李四',
    avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
    lastMessage: '好的，我知道了',
    lastMessageTime: new Date(Date.now() - 3600000), // 1小时前
    unreadCount: 0,
    status: '1小时前在线',
    online: false,
    conversation_id: 'private_2'
  },
  {
    id: 3,
    name: '王五',
    avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
    lastMessage: '明天见！',
    lastMessageTime: new Date(Date.now() - 86400000), // 昨天
    unreadCount: 1,
    status: '昨天在线',
    online: false,
    conversation_id: 'private_3'
  }
])

// 群聊联系人列表
const groupContacts = ref([
  {
    id: 101,
    name: '开发团队',
    avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
    lastMessage: '[张三] 今天的任务完成了',
    lastMessageTime: new Date(Date.now() - 1800000), // 30分钟前
    unreadCount: 5,
    status: '5人在线',
    type: 'group'
  },
  {
    id: 102,
    name: '产品讨论组',
    avatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
    lastMessage: '[李四] 新功能已经上线',
    lastMessageTime: new Date(Date.now() - 7200000), // 2小时前
    unreadCount: 0,
    status: '12人在线',
    type: 'group'
  }
])

// 其他联系人列表
const otherContacts = ref([
  {
    id: 'other1',
    name: '其他',
    avatar: '',
    subtitle: '(待设置团队门门)',
    online: false,
    unreadCount: 0,
    conversation_id: 'other_1'
  },
  {
    id: 'user5',
    name: '戴法晶',
    avatar: '',
    online: true,
    lastMessageTime: '2023-09-16T16:52:00',
    lastMessage: '大家好！我是戴老师，我来了。',
    unreadCount: 0,
    conversation_id: 'private_user5'
  },
  {
    id: 'user6',
    name: 'msgyudi',
    avatar: '',
    online: false,
    unreadCount: 0,
    conversation_id: 'private_user6'
  },
  {
    id: 'user7',
    name: 'Roc',
    avatar: '',
    online: false,
    unreadCount: 0,
    conversation_id: 'private_user7'
  },
  {
    id: 'user8',
    name: '郑减',
    avatar: '',
    online: false,
    unreadCount: 0,
    conversation_id: 'private_user8'
  },
  {
    id: 'user9',
    name: '玉成',
    avatar: '',
    online: false,
    unreadCount: 0,
    conversation_id: 'private_user9'
  },
  {
    id: 'user10',
    name: 'Cantin',
    avatar: '',
    online: false,
    unreadCount: 0,
    conversation_id: 'private_user10'
  },
  {
    id: 'user11',
    name: '微信联系人',
    avatar: '',
    online: false,
    unreadCount: 0,
    conversation_id: 'private_user11'
  },
  {
    id: 'user12',
    name: '柯南',
    avatar: '',
    online: false,
    unreadCount: 0,
    conversation_id: 'private_user12'
  },
  {
    id: 'user13',
    name: '孙家勇',
    avatar: '',
    online: false,
    unreadCount: 0,
    conversation_id: 'private_user13'
  }
])

// 使用ActionCable的消息列表
const messages = computed(() => chatMessages.value || [])

// 正在输入的用户列表
const typingUsersList = computed(() => {
  if (!activeContact.value || !typingUsers.value || typingUsers.value.size === 0 || !currentUser.value) {
    return []
  }
  return Array.from(typingUsers.value).filter(userId => userId !== currentUser.value.id)
})

// 输入状态文本
const typingStatusText = computed(() => {
  const count = typingUsersList.value.length
  if (count === 0) return ''
  if (count === 1) return '对方正在输入...'
  return `${count}人正在输入...`
})

// 处理导航变化
const handleNavChange = (section) => {
  console.log('导航切换到:', section)
}

// 选择联系人
const selectContact = (contact) => {
  // 停止之前联系人的输入状态（只有在有当前频道时才发送）
  if (activeContact.value && activeContact.value.conversation_id && isConnected.value) {
    // 只有在频道已订阅的情况下才发送停止输入状态
    sendTypingStatus && sendTypingStatus(activeContact.value.conversation_id, false)
  }
  
  activeContact.value = contact
  
  // 加载该联系人的消息历史
  if (contact.conversation_id) {
    loadConversationMessages && loadConversationMessages(contact.conversation_id)
  }
  
  // 标记消息为已读
  if (contact.unreadCount > 0) {
    markContactMessagesAsRead(contact)
  }
}

// 标记联系人消息为已读
const markContactMessagesAsRead = (contact) => {
  // 重置未读计数
  contact.unreadCount = 0
  
  // 通过ActionCable标记消息为已读
  if (contact.conversation_id) {
    // 这里可以发送标记已读的请求
    console.log(`标记会话 ${contact.conversation_id} 的消息为已读`)
  }
}

// 更新联系人最后消息
const updateContactLastMessage = (message) => {
  const contact = [...privateContacts.value, ...groupContacts.value, ...otherContacts.value]
    .find(c => c.conversation_id === message.conversation_id)
  
  if (contact) {
    contact.lastMessage = message.content
    contact.lastMessageTime = new Date(message.timestamp)
    
    // 如果不是当前活跃联系人，增加未读计数
    if (activeContact.value?.id !== contact.id && message.sender_id !== currentUser.value.id) {
      contact.unreadCount = (contact.unreadCount || 0) + 1
    }
  }
}

// 发送消息
const sendMessage = () => {
  if (!newMessage.value.trim() || !activeContact.value || !isConnected.value) {
    return
  }
  
  const content = newMessage.value.trim()
  const conversationId = activeContact.value.conversation_id || `private_${activeContact.value.id}`
  
  // 通过ActionCable发送消息
  const success = sendChatMessage(content, conversationId)
  
  if (success) {
    // 清空输入框
    newMessage.value = ''
    
    // 停止输入状态
    sendTypingStatus && sendTypingStatus(conversationId, false)
    
    // 滚动到底部
    nextTick(() => {
      scrollToBottom && scrollToBottom()
    })
  } else {
    console.error('发送消息失败：连接未建立或消息为空')
  }
}

// 输入状态管理
let typingTimer = null
const handleInputChange = () => {
  if (!activeContact.value || !isConnected.value || !activeContact.value.conversation_id) {
    return
  }
  
  const conversationId = activeContact.value.conversation_id
  
  // 发送开始输入状态（只有在频道已订阅的情况下）
  if (sendTypingStatus) {
    sendTypingStatus(conversationId, true)
  }
  
  // 清除之前的定时器
  if (typingTimer) {
    clearTimeout(typingTimer)
  }
  
  // 3秒后自动停止输入状态
  typingTimer = setTimeout(() => {
    if (sendTypingStatus && activeContact.value?.conversation_id) {
      sendTypingStatus(activeContact.value.conversation_id, false)
    }
  }, 3000)
}

// 处理回车键
const handleEnterPress = (event) => {
  if (event.ctrlKey || event.metaKey) {
    // Ctrl+Enter 或 Cmd+Enter 发送消息
    event.preventDefault()
    sendMessage()
  }
  // 普通 Enter 默认换行，不需要特殊处理
}

// 生命周期钩子
onMounted(async () => {
  // 连接 ActionCable
  if (connect) {
    await connect()
  }
  
  // 选择第一个私聊联系人
  if (privateContacts.value.length > 0) {
    selectContact(privateContacts.value[0])
  }
  
  // 监听消息变化，自动滚动到底部
  watch(chatMessages, () => {
    nextTick(() => {
      scrollToBottom && scrollToBottom()
    })
  }, { deep: true })
  
  // 监听活跃联系人变化，动态订阅频道
  watch(activeContact, (newContact, oldContact) => {
    if (oldContact && oldContact.conversation_id && instance?.proxy?.$cable) {
      // 取消之前的订阅
      const oldChannelName = `ChatChannel_${oldContact.conversation_id}`
      try {
        instance.proxy.$cable.unsubscribe(oldChannelName)
        console.log('取消订阅频道:', oldContact.conversation_id)
      } catch (error) {
        console.error('取消订阅失败:', error)
      }
    }
    
    if (newContact && newContact.conversation_id && instance?.proxy?.$cable && isConnected.value) {
      // 订阅新的频道
      const channelParams = {
        channel: 'ChatChannel',
        conversation_id: newContact.conversation_id,
        user_id: currentUserId.value
      }
      
      const channelName = `ChatChannel_${newContact.conversation_id}`
      
      try {
        instance.proxy.$cable.subscribe(channelParams, channelName, {
          connected() {
            console.log('聊天频道连接成功:', newContact.conversation_id)
          },
          
          disconnected() {
            console.log('聊天频道断开:', newContact.conversation_id)
          },
          
          received(data) {
            handleReceivedMessage && handleReceivedMessage(data)
          },
          
          rejected() {
            console.error('聊天频道订阅被拒绝:', newContact.conversation_id)
          }
        })
        console.log('订阅新频道:', newContact.conversation_id)
      } catch (error) {
        console.error('订阅频道失败:', error)
      }
    }
  }, { immediate: true })
})

onUnmounted(() => {
  // 断开ActionCable连接
  if (disconnect) {
    disconnect()
  }
  
  // 清理定时器
  if (typingTimer) {
    clearTimeout(typingTimer)
  }
})

// 格式化时间
const formatTime = (timestamp) => {
  const now = new Date()
  const time = new Date(timestamp)
  const diffMs = now - time
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) {
    return time.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  } else if (diffDays === 1) {
    return '昨天'
  } else if (diffDays < 7) {
    return `${diffDays}天前`
  } else {
    return time.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' })
  }
}

// 格式化消息时间
const formatMessageTime = (timestamp) => {
  return new Date(timestamp).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
}

// 初始化时选择第一个联系人
// 注意：这部分逻辑已经移到上面的 onMounted 钩子中
</script>

<style scoped>
/* Tab样式定制 */
.chat-tabs :deep(.el-tabs__header) {
  margin-bottom: 0;
}

.chat-tabs :deep(.el-tabs__nav) {
  width: 100%;
  display: flex;
}

.chat-tabs :deep(.el-tabs__item) {
  flex: 1;
}
</style>
